import { configureStore } from '@reduxjs/toolkit';
import { LoadingStatuses } from 'core/common/entities';
import { config } from 'core/config';
import { featureFlagsAttrObserver } from 'core/feature-flags/store';
import { getCurrentLanguage } from 'core/localization/utils/getCurrentLanguage';
import { offersFeatureObserver } from 'core/offers/store/enhancer';
import { markUtmSourceForIncomingUser } from 'core/user/store/actions';
import { UserUtmTagsState } from 'core/user/store/state';
import { Services } from '../contexts';
import { getIsDevMode } from '../utils/getIsDevMode';
import rootReducer, { RootInitialState } from './rootReducer';
import { AppDispatch } from './types';

export default function createStore(services: Services) {
  const { appEnvironment, utmTagsService, storePersistenceService, featureFlags, locationService } =
    services;

  const system = {
    isTestingEnv: appEnvironment.isTestingEnv(),
    language: getCurrentLanguage(),
    isApplePaySupported: appEnvironment.isApplePaySupported(),
    isAppleMobile: appEnvironment.isAppleMobile(),
    isMobileDevice: appEnvironment.isMobileDevice(),
    platform: appEnvironment.getPlatform(),
    project: appEnvironment.getProjectName(),
  };

  const userUtmTags: UserUtmTagsState = {
    tags: utmTagsService.getUtmTags({ storage: 'session' }),
  };

  const initialLocation = locationService.readCookieLocation();
  const preloadedState: Partial<RootInitialState> = {
    ...storePersistenceService.load(),
    system,
    userUtmTags,
    ...(initialLocation && {
      userLocation: {
        location: initialLocation,
        loadingStatus: LoadingStatuses.FULFILLED,
      },
    }),
  };

  const enhancers = [offersFeatureObserver(featureFlags), featureFlagsAttrObserver(featureFlags)];

  const store = configureStore({
    devTools: !config.isProduction || getIsDevMode(),
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: { ...services, featureFlags },
        },
      }),
    enhancers: (getDefaultEnhancers) => {
      return getDefaultEnhancers().concat(enhancers);
    },
  });
  const dispatch = store.dispatch as AppDispatch;

  dispatch(markUtmSourceForIncomingUser());

  store.subscribe(() => {
    const { ...state } = store.getState();
    storePersistenceService.save(state);
  });

  return store;
}
